import React, { useState, useEffect } from "react";
import axios from "axios";
import { Table, Button, Modal, Form, Alert } from "react-bootstrap";

const API_URL = process.env.REACT_APP_API_URL;

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [needsRefresh, setNeedsRefresh] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentUser, setCurrentUser] = useState({
    user_id: null,
    user: "",
    email: "",
    password: "",
    type: "manager",
    team_id: "",
    display_name: "",
  });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [usersRes, teamsRes] = await Promise.all([
          axios.get(`${API_URL}/users`),
          axios.get(`${API_URL}/teams/all`),
        ]);
        setUsers(usersRes.data);
        setTeams(teamsRes.data);
      } catch (err) {
        setError("Không thể tải dữ liệu người dùng.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [needsRefresh]);

  const handleClose = () => {
    setShowModal(false);
    setCurrentUser({
      user_id: null,
      user: "",
      email: "",
      password: "",
      type: "manager",
      team_id: "",
      display_name: "",
    });
    setError("");
  };

  const handleShowAdd = () => {
    setIsEditing(false);
    handleClose();
    setShowModal(true);
  };

  const handleShowEdit = (user) => {
    setIsEditing(true);
    setCurrentUser({ ...user, password: "" }); // Xóa password để không hiển thị
    setShowModal(true);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setCurrentUser((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      if (isEditing) {
        await axios.put(`${API_URL}/users/${currentUser.user_id}`, currentUser);
      } else {
        await axios.post(`${API_URL}/users`, currentUser);
      }
      setNeedsRefresh((p) => !p);
      handleClose();
    } catch (err) {
      setError(err.response?.data || "Đã xảy ra lỗi.");
    }
  };

  const handleDelete = async (userId) => {
    if (window.confirm("Bạn có chắc chắn muốn xóa người dùng này?")) {
      try {
        await axios.delete(`${API_URL}/users/${userId}`);
        setNeedsRefresh((p) => !p);
      } catch (err) {
        setError(err.response?.data || "Lỗi khi xóa người dùng.");
      }
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Quản lý Người dùng</h2>
        <Button variant="primary" onClick={handleShowAdd}>
          Thêm User
        </Button>
      </div>
      {error && (
        <Alert variant="danger" onClose={() => setError("")} dismissible>
          {error}
        </Alert>
      )}
      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th>User ID</th>
            <th>Tên đăng nhập</th>
            <th>Tên hiển thị</th>
            <th>Email</th>
            <th>Vai trò</th>
            <th>Đội quản lý</th>
            <th>Hành động</th>
          </tr>
        </thead>
        <tbody>
          {users.map((user) => (
            <tr key={user.user_id}>
              <td>{user.user_id}</td>
              <td>{user.user}</td>
              <td>{user.display_name}</td>
              <td>{user.email}</td>
              <td>{user.type}</td>
              <td>{user.type === "manager" ? user.team_name : "N/A"}</td>
              <td>
                <Button
                  variant="warning"
                  size="sm"
                  onClick={() => handleShowEdit(user)}
                  className="me-2"
                >
                  Sửa
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => handleDelete(user.user_id)}
                >
                  Xóa
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <Modal show={showModal} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>{isEditing ? "Sửa User" : "Thêm User"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Tên đăng nhập</Form.Label>
              <Form.Control
                type="text"
                name="user"
                value={currentUser.user}
                onChange={handleFormChange}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Tên hiển thị</Form.Label>
              <Form.Control
                type="text"
                name="display_name"
                value={currentUser.display_name}
                onChange={handleFormChange}
                placeholder="Để trống sẽ dùng tên đăng nhập"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                name="email"
                value={currentUser.email}
                onChange={handleFormChange}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Mật khẩu</Form.Label>
              <Form.Control
                type="password"
                name="password"
                value={currentUser.password}
                onChange={handleFormChange}
                placeholder={isEditing ? "Để trống nếu không muốn đổi" : ""}
                required={!isEditing}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Vai trò</Form.Label>
              <Form.Select
                name="type"
                value={currentUser.type}
                onChange={handleFormChange}
              >
                <option value="admin">Admin</option>
                <option value="bks">BKS</option>
                <option value="manager">Manager</option>
              </Form.Select>
            </Form.Group>
            {currentUser.type === "manager" && (
              <Form.Group className="mb-3">
                <Form.Label>Đội quản lý</Form.Label>
                <Form.Select
                  name="team_id"
                  value={currentUser.team_id}
                  onChange={handleFormChange}
                  required
                >
                  <option value="">-- Chọn đội --</option>
                  {teams.map((team) => (
                    <option key={team.id} value={team.id}>
                      {team.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            )}
            <div className="text-end">
              <Button
                variant="secondary"
                onClick={handleClose}
                className="me-2"
              >
                Hủy
              </Button>
              <Button variant="primary" type="submit">
                Lưu
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default UserManagement;
